import { isEqual } from 'lodash';
import React, { FC, useMemo } from 'react';
import { ProjectId } from 'api/models';
import { FieldWithValue } from 'api/tasks';
import type { Option } from 'components/atoms/select/types';
import { BaseSelect } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields/fields/base-select';
import type { SingleUserField as SingleUserFieldT } from 'components/organisms/tasks/fields';
import { useGetUserByUserId } from 'hooks/use-idb';
import { useUserFieldOptions } from 'hooks/use-user-field-options';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';

interface FieldProps {
  field: SingleUserFieldT;
  disabled?: boolean;
  onChange?: (selected: number | null, field: FieldWithValue) => void;
  projectId?: number;
}

export const SingleUserField: FC<FieldProps> = ({
  field,
  disabled = false,
  onChange,
  projectId,
}) => {
  const project = ProjectId(projectId ?? 0);
  const selectedUser = useGetUserByUserId(field.value);
  const options = useUserFieldOptions(
    {
      projectId: project,
    },
    field,
  );

  const selectedUserOption = useMemo(
    () =>
      selectedUser
        ? {
            label: fullName(selectedUser),
            description: selectedUser.role?.roleName,
            imageUrl: selectedUser.photoUrl,
            value: selectedUser.userId,
            color: getRandomColor(selectedUser.userId),
            userStatus: selectedUser.userStatus,
          }
        : undefined,
    [selectedUser],
  );

  const commit = (selected: Option | null) => {
    if (onChange && !isEqual(selected?.value, field.value)) {
      onChange(selected ? Number(selected.value) : selected, {
        ...field,
        fieldDataType: 'USER',
        optionsAvailable: true,
      } as FieldWithValue);
    }
  };

  return (
    <BaseSelect
      type="user"
      grouped
      disabled={disabled}
      options={options}
      value={selectedUserOption}
      onChange={commit}
      data-cy={`right.content.single.user${field.fieldLabel}`}
    />
  );
};
